import type { i18n as I18nInstance, ThirdPartyModule } from 'i18next';

/**
 * Módulo de i18next que da regla de plural a los códigos de idioma internos de la app.
 *
 * i18next elige la clave `_plural` según su tabla de reglas por código de idioma (`ca`, `es`,
 * `eu`, `gl`…). Los códigos internos `vl` (valenciano) y `mx` (español de México) no son ISO y no
 * están en esa tabla, así que sin esto nunca pluralizan: «9 document», «0 prova» (TAN-10614).
 *
 * Se registra con `i18n.use(internalLanguagesPluralRules)` antes de `init`, igual que
 * `initReactI18next`.
 */

/**
 * Idioma del que cada código interno hereda la regla de plural. Es la única tabla que hay que
 * tocar para dar de alta un código nuevo: sin pareja no compila, así que no puede caer en un
 * idioma por defecto sin querer.
 */
const PLURAL_BASE_LANGUAGE = {
  vl: 'ca', // valenciano → catalán (en BCP 47 sería `ca-valencia`)
  mx: 'es', // español de México → español
} as const;

/** Códigos internos sin regla de plural en i18next. */
export const INTERNAL_LANGUAGE_CODES = Object.keys(PLURAL_BASE_LANGUAGE) as (keyof typeof PLURAL_BASE_LANGUAGE)[];

type PluralRule = { numbers: number[]; plurals: (count: number) => number };

/** `i18n.services.pluralResolver` está tipado como `any` en i18next 19. */
type PluralResolver = {
  addRule: (lng: string, rule: PluralRule) => void;
  getRule: (code: string) => PluralRule | undefined;
};

export const internalLanguagesPluralRules: ThirdPartyModule = {
  type: '3rdParty',
  init(i18n: I18nInstance): void {
    const pluralResolver = i18n.services.pluralResolver as PluralResolver;

    INTERNAL_LANGUAGE_CODES.forEach((code) => {
      if (pluralResolver.getRule(code)) return;
      const baseRule = pluralResolver.getRule(PLURAL_BASE_LANGUAGE[code]);
      if (baseRule) pluralResolver.addRule(code, baseRule);
    });
  },
};
